import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Divider, Flex, Menu, Typography } from 'antd';
import { settingsMenu } from '../../../constant/app/settings';
import { useSelector } from 'react-redux';

const { SubMenu } = Menu;
const { Text, Title } = Typography;

// Define colors for styling
const defaultIconColor = "#8C8C8C"; // Gray
const selectedIconColor = "#00A85A"; // Dark Green
const selectedTextColor = "#00A85A"; // Dark Green
const selectedBgColor = "#E6F7EE"; // Light Green

function Settings({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const featureFlags = useSelector((state) => state.app.featureFlags);
  const isKeywordTriggerEnabled = featureFlags?.keyword_trigger_feature;
  
  // Filter menu items based on feature flags
  const filteredMenu = settingsMenu.filter(item => {
    if (item.label === 'keywords') {
      return isKeywordTriggerEnabled;
    }
    return true; // Show all other menu items
  });

  // Set initial open keys and default navigation
  useEffect(() => {
    // Get all menu items with submenus from the filtered menu
    const menuItemsWithSubmenus = filteredMenu.filter(item => item.subMenu?.length > 0);
    
    // If there are items with submenus, open them all by default
    if (menuItemsWithSubmenus.length > 0) {
      const defaultOpenKeys = menuItemsWithSubmenus.map(item => 
        item.link || item.id.toString()
      );
      setOpenKeys(defaultOpenKeys);
      
      // If no item is selected, navigate to the first available submenu item
      if (!pathname.includes('/settings/')) {
        // Find the first menu item that has submenus and get its first submenu item
        for (const menuItem of filteredMenu) {
          if (menuItem.subMenu?.length > 0) {
            const firstSubmenuItem = menuItem.subMenu[0]?.link;
            if (firstSubmenuItem) {
              navigate(firstSubmenuItem);
              break;
            }
          } else if (menuItem.link) {
            // If it's a direct link (no submenu), navigate to it
            navigate(menuItem.link);
            break;
          }
        }
      }
    } else if (filteredMenu.length > 0 && !pathname.includes('/settings/')) {
      // If there are no submenus but there are menu items, navigate to the first one
      const firstMenuItem = filteredMenu[0];
      if (firstMenuItem.link) {
        navigate(firstMenuItem.link);
      }
    }
  }, []); // Empty dependency array to run only once on mount

  const handleMenuClick = (link) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <Flex className="h-full">
      <Flex className="h-screen">
        <Flex vertical className="bg-[#fff] w-64 border-r border-gray-200">
          <div className="px-4 pt-4">
            <Title level={4} style={{ marginBottom: "10px" }}>
              Settings
            </Title>
          </div>
          <Divider className="m-0 mb-4" />
          <div className="override-default-submenu-styles-settings-submenu">
            <Menu
              mode="inline"
              style={{ border: "none" }}
              selectedKeys={[pathname]}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              className="px-2"
            >
              {filteredMenu.map((item) =>
                item.subMenu && item.subMenu.length > 0 ? (
                  <SubMenu
                    key={item.link || item.id.toString()}
                    className="bg-white"
                    title={
                      <Flex align="center" gap={8}>
                        {item.icon && (
                          <span 
                            className="text-xs"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 20,
                              height: 20,
                              color: pathname.includes(item.link) ? selectedIconColor : defaultIconColor
                            }}
                          >
                            {React.createElement(item.icon, {
                              style: {
                                width: '100%',
                                height: '100%',
                                fill: 'currentColor'
                              }
                            })}
                          </span>
                        )}
                        <Text className="text-xs" strong>
                          {item.name}
                        </Text>
                      </Flex>
                    }
                  >
                    {item.subMenu.map((subItem) => {
                      const isSelected = pathname === subItem.link;
                      
                      return (
                        <Menu.Item
                          key={subItem.link}
                          className="group hover:bg-gray-200"
                          onClick={() => handleMenuClick(subItem.link)}
                          style={{
                            backgroundColor: isSelected ? selectedBgColor : 'transparent',
                            borderRadius: "6px",
                            margin: "4px 0",
                          }}
                        >
                          <Flex align="center" gap={8}>
                            {subItem.icon && (
                              <span 
                                className="text-base"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 16,
                                  height: 16,
                                  color: isSelected ? selectedIconColor : defaultIconColor
                                }}
                              >
                                {React.createElement(subItem.icon, {
                                  style: {
                                    width: '100%',
                                    height: '100%',
                                    fill: 'currentColor'
                                  }
                                })}
                              </span>
                            )}
                            <Text
                              style={{
                                color: isSelected ? selectedTextColor : '#595959',
                              }}
                              className="text-sm group-hover:text-black"
                            >
                              {subItem.name}
                            </Text>
                          </Flex>
                        </Menu.Item>
                      );
                    })}
                  </SubMenu>
                ) : (
                  <Menu.Item
                    key={item.link || item.id.toString()}
                    onClick={() => handleMenuClick(item.link)}
                    className={`${pathname === item.link ? '!bg-[rgb(230,247,238)]' : ''}`}
                  >
                    <Flex align="center" gap={8}>
                      {item.icon && (
                        <span className="flex items-center justify-center w-5 h-5">
                          {item.iconType === 'antd' ? (
                            <item.icon 
                              style={{
                                fontSize: '18px',
                                color: pathname === item.link ? selectedTextColor : '#000000'
                              }}
                            />
                          ) : (
                            <item.icon 
                              size={24}
                              height={24}
                              width={24}
                              color={pathname === item.link ? selectedTextColor : '#000000'}
                            />
                          )}
                        </span>
                      )}
                      <Text 
                        className="!text-[0.8rem]" 
                        style={{ 
                          color: pathname === item.link ? selectedTextColor : '#000000',
                        }}
                      >
                        {item.name}
                      </Text>
                    </Flex>
                  </Menu.Item>
                ),
              )}
            </Menu>
          </div>
        </Flex>
      </Flex>
      <Flex
        vertical
        className="h-screen p-6 w-full min-w-[800px] overflow-auto"
      >
        <Outlet />
        {children}
      </Flex>
    </Flex>
  );
}

export default Settings;
